import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Button,
  Grid,
  Divider,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  SelectChangeEvent,
} from '@mui/material';
import {
  LocalHospital as DoctorIcon,
  Person as OfficerIcon,
  ArrowForward as ArrowForwardIcon,
  HealthAndSafety as HealthIcon,
  Security as SecurityIcon,
  Verified as VerifiedIcon,
  Star as StarIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  Badge as BadgeIcon,
  Work as WorkIcon,
  LocationOn as LocationIcon,
  ContactPhone as ContactIcon,
  Favorite as MedicalIcon,
  ExpandMore as ExpandMoreIcon,
  ArrowBack as BackIcon,
  CheckCircle as CheckIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import axios from 'axios';

// Constants for Officer Form
const CADRES = [
  { code: 'IAS', name: 'Indian Administrative Service' },
  { code: 'IPS', name: 'Indian Police Service' },
  { code: 'IFS', name: 'Indian Foreign Service' },
  { code: 'IRS', name: 'Indian Revenue Service' },
  { code: 'IRTS', name: 'Indian Railway Traffic Service' },
  { code: 'IPoS', name: 'Indian Postal Service' },
  { code: 'IFoS', name: 'Indian Forest Service' },
  { code: 'JUDGE', name: 'Judge' },
  { code: 'MINISTER', name: 'Minister' },
  { code: 'OSD', name: 'Officer on Special Duty' },
  { code: 'SENIOR_GOVERNMENT_OFFICER', name: 'Senior Government Officer' },
];

const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-', 'Unknown'];

const STEPS_OFFICER = ['Account Details', 'Service Details', 'Address & Emergency', 'Medical Info', 'Review & Submit'];

/**
 * Role Selection Page for Registration
 * Premium Enterprise UI/UX Design
 */
const RegisterPage: React.FC = () => {
  const navigate = useNavigate();

  // Officer Form State
  const [showOfficerForm, setShowOfficerForm] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [checking, setChecking] = useState<{ [key: string]: boolean }>({});

  const [formData, setFormData] = useState({
    username: '', email: '', mobile: '', password: '', confirmPassword: '', fullName: '',
    employeeId: '', cadre: '', designation: '', postingLocation: '', batchYear: '',
    joiningDate: '', gradePay: '', payLevel: '', department: '',
    residenceAddressLine1: '', residenceAddressLine2: '', residenceCity: '',
    residenceDistrict: '', residenceState: '', residencePincode: '', residenceLandmark: '',
    emergencyContactName: '', emergencyContactRelation: '', emergencyContactPhone: '',
    bloodGroup: '', allergies: '', chronicConditions: '', currentMedications: '',
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const roles = [
    {
      id: 'doctor',
      title: 'Doctor Registration',
      subtitle: 'Government Medical Officer',
      icon: <DoctorIcon sx={{ fontSize: 48 }} />,
      color: '#2563EB',
      gradient: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
      description: 'Register as a Government Doctor to provide healthcare services to officers and their families.',
      features: [
        'Manage appointments & schedules',
        'Create digital prescriptions',
        'Track patient visits & history',
        'Submit medical reports',
        'Real-time dashboard & analytics',
      ],
      requirements: [
        'Valid MCI Registration Number',
        'Government Employee ID',
        'MBBS/MD/MS Qualification',
        'Medical Council Certificate',
      ],
      onClick: () => navigate('/register/doctor'),
      premium: true,
    },
    {
      id: 'officer',
      title: 'Officer Registration',
      subtitle: 'Government Officer / Family Member',
      icon: <OfficerIcon sx={{ fontSize: 48 }} />,
      color: '#10B981',
      gradient: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
      description: 'Register as a Government Officer or Family Member to access premium healthcare services.',
      features: [
        'Book appointments with doctors',
        'Track medical records',
        'Family member management',
        'Prescription history',
        'Health notifications & reminders',
      ],
      requirements: [
        'Valid Government Employee ID',
        'Active Government Service',
        'Aadhaar Card',
        'Official Email Address',
      ],
      onClick: () => navigate('/register/officer'),
      premium: false,
    },
  ];

  // Officer Form Handlers
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const name = e.target.name as string;
    const value = e.target.value as string;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const checkAvailability = async (field: string, value: string) => {
    if (!value.trim()) return;
    setChecking((prev) => ({ ...prev, [field]: true }));
    try {
      const endpoints: { [key: string]: string } = {
        username: `/api/v1/registration/check-username/${value}`,
        email: `/api/v1/registration/check-email/${value}`,
        mobile: `/api/v1/registration/check-mobile/${value}`,
        employeeId: `/api/v1/registration/check-employee-id/${value}`,
      };
      if (!endpoints[field]) return;
      const response = await axios.get(`${import.meta.env.VITE_API_URL || 'http://localhost:8082/api'}${endpoints[field]}`);
      if (!response.data.data) {
        setErrors((prev) => ({ ...prev, [field]: `This ${field} is already taken` }));
      }
    } catch (err) {
      console.error('Error checking availability:', err);
    } finally {
      setChecking((prev) => ({ ...prev, [field]: false }));
    }
  };

  const validateStep = (step: number): boolean => {
    const newErrors: { [key: string]: string } = {};
    if (step === 0) {
      if (!formData.username.trim()) newErrors.username = 'Username is required';
      else if (formData.username.length < 3) newErrors.username = 'Username must be at least 3 characters';
      if (!formData.email.trim()) newErrors.email = 'Email is required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email format';
      if (!formData.mobile.trim()) newErrors.mobile = 'Mobile number is required';
      else if (!/^[6-9]\d{9}$/.test(formData.mobile)) newErrors.mobile = 'Invalid mobile number';
      if (!formData.password) newErrors.password = 'Password is required';
      else if (formData.password.length < 8) newErrors.password = 'Password must be at least 8 characters';
      if (!formData.confirmPassword) newErrors.confirmPassword = 'Please confirm your password';
      else if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
      if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    }
    if (step === 1) {
      if (!formData.employeeId.trim()) newErrors.employeeId = 'Employee ID is required';
      if (!formData.cadre) newErrors.cadre = 'Please select a cadre';
      if (!formData.designation.trim()) newErrors.designation = 'Designation is required';
    }
    if (step === 2) {
      if (formData.residencePincode && !/^\d{6}$/.test(formData.residencePincode))
        newErrors.residencePincode = 'Invalid pincode';
      if (formData.emergencyContactPhone && !/^[6-9]\d{9}$/.test(formData.emergencyContactPhone))
        newErrors.emergencyContactPhone = 'Invalid mobile number';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => setActiveStep((prev) => prev - 1);

  const handleSubmit = async () => {
    if (!validateStep(activeStep)) return;
    setLoading(true);
    setError('');

    try {
      const payload = {
        username: formData.username, email: formData.email, mobile: formData.mobile,
        password: formData.password, confirmPassword: formData.confirmPassword, fullName: formData.fullName,
        employeeId: formData.employeeId, cadre: formData.cadre, designation: formData.designation,
        department: formData.department || null, postingLocation: formData.postingLocation || null,
        batchYear: formData.batchYear ? parseInt(formData.batchYear) : null,
        joiningDate: formData.joiningDate || null, gradePay: formData.gradePay || null, payLevel: formData.payLevel || null,
        residenceAddressLine1: formData.residenceAddressLine1 || null,
        residenceAddressLine2: formData.residenceAddressLine2 || null,
        residenceCity: formData.residenceCity || null, residenceDistrict: formData.residenceDistrict || null,
        residenceState: formData.residenceState || null, residencePincode: formData.residencePincode || null,
        residenceLandmark: formData.residenceLandmark || null,
        emergencyContactName: formData.emergencyContactName || null,
        emergencyContactRelation: formData.emergencyContactRelation || null,
        emergencyContactPhone: formData.emergencyContactPhone || null,
        bloodGroup: formData.bloodGroup || null, allergies: formData.allergies || null,
        chronicConditions: formData.chronicConditions || null, currentMedications: formData.currentMedications || null,
      };

      const response = await axios.post(`${import.meta.env.VITE_API_URL || 'http://localhost:8082/api'}/v1/registration/officer`, payload);
      if (response.data.success) setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Success Screen for Officer
  if (success) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'transparent' , p: 3 }}>
        <Paper sx={{ p: 4, maxWidth: 500, textAlign: 'center', borderRadius: 3 }}>
          <CheckIcon sx={{ fontSize: 80, color: '#10b981', mb: 2 }} />
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: '#1e293b' }}>Registration Successful!</Typography>
          <Typography variant="body1" sx={{ color: '#64748b', mb: 3 }}>
            Your account is pending verification. Our team will verify your credentials within 24-48 hours.
          </Typography>
          <Button variant="contained" size="large" onClick={() => navigate('/login')} sx={{ borderRadius: 2 }}>Go to Login</Button>
        </Paper>
      </Box>
    );
  }

  // Officer Registration Form
  if (showOfficerForm) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'transparent' , py: 4, px: { xs: 2, md: 4 } }}>
        <Box sx={{ maxWidth: 900, mx: 'auto' }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
              <BusinessIcon sx={{ fontSize: 36, color: '#10b981' }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b' }}>Officer Registration</Typography>
            </Box>
            <Button size="small" onClick={() => { setShowOfficerForm(false); setActiveStep(0); }} sx={{ color: '#64748b' }}>← Change Role</Button>
          </Box>

          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {STEPS_OFFICER.map((label) => (<Step key={label}><StepLabel>{label}</StepLabel></Step>))}
          </Stepper>

          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

          <Paper sx={{ p: 4, borderRadius: 3 }}>
            {/* Step 0: Account Details */}
            {activeStep === 0 && (
              <Grid container spacing={3}>
                <Grid item xs={12}><Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1e293b' }}>Account Information</Typography></Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Username" name="username" value={formData.username} onChange={handleChange}
                    onBlur={() => checkAvailability('username', formData.username)} error={!!errors.username}
                    helperText={errors.username || 'Letters, numbers, dots, underscores, hyphens allow'}
                    InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon sx={{ color: '#94a3b8' }} /></InputAdornment>, endAdornment: checking.username && <CircularProgress size={20} /> }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Email Address" name="email" type="email" value={formData.email} onChange={handleChange}
                    onBlur={() => checkAvailability('email', formData.email)} error={!!errors.email} helperText={errors.email}
                    InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon sx={{ color: '#94a3b8' }} /></InputAdornment>, endAdornment: checking.email && <CircularProgress size={20} /> }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Mobile Number" name="mobile" value={formData.mobile} onChange={handleChange}
                    onBlur={() => checkAvailability('mobile', formData.mobile)} error={!!errors.mobile} helperText={errors.mobile || '10 digit mobile number'}
                    InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ color: '#94a3b8' }} /></InputAdornment>, endAdornment: checking.mobile && <CircularProgress size={20} /> }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Full Name" name="fullName" value={formData.fullName} onChange={handleChange} error={!!errors.fullName} helperText={errors.fullName}
                    InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon sx={{ color: '#94a3b8' }} /></InputAdornment> }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Password" name="password" type={showPassword ? 'text' : 'password'} value={formData.password} onChange={handleChange} error={!!errors.password} helperText={errors.password}
                    InputProps={{ startAdornment: <InputAdornment position="start"><LockIcon sx={{ color: '#94a3b8' }} /></InputAdornment>,
                      endAdornment: (<InputAdornment position="end"><IconButton onClick={() => setShowPassword(!showPassword)} edge="end">{showPassword ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment>) }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Confirm Password" name="confirmPassword" type={showConfirmPassword ? 'text' : 'password'} value={formData.confirmPassword} onChange={handleChange} error={!!errors.confirmPassword} helperText={errors.confirmPassword}
                    InputProps={{ startAdornment: <InputAdornment position="start"><LockIcon sx={{ color: '#94a3b8' }} /></InputAdornment>,
                      endAdornment: (<InputAdornment position="end"><IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">{showConfirmPassword ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment>) }} />
                </Grid>
              </Grid>
            )}

            {/* Step 1: Service Details */}
            {activeStep === 1 && (
              <Grid container spacing={3}>
                <Grid item xs={12}><Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1e293b' }}>Service Information</Typography></Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Employee ID" name="employeeId" value={formData.employeeId} onChange={handleChange}
                    onBlur={() => checkAvailability('employeeId', formData.employeeId)} error={!!errors.employeeId} helperText={errors.employeeId || 'Your official employee ID'}
                    InputProps={{ startAdornment: <InputAdornment position="start"><BadgeIcon sx={{ color: '#94a3b8' }} /></InputAdornment>, endAdornment: checking.employeeId && <CircularProgress size={20} /> }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth error={!!errors.cadre}><InputLabel>Cadre *</InputLabel>
                    <Select name="cadre" value={formData.cadre} onChange={handleSelectChange} label="Cadre *">
                      <MenuItem value="">Select Cadre</MenuItem>
                      {CADRES.map((c) => (<MenuItem key={c.code} value={c.code}>{c.name}</MenuItem>))}
                    </Select>
                    {errors.cadre && <FormHelperText>{errors.cadre}</FormHelperText>}
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Designation" name="designation" value={formData.designation} onChange={handleChange} error={!!errors.designation} helperText={errors.designation || 'e.g., District Collector'}
                    InputProps={{ startAdornment: <InputAdornment position="start"><WorkIcon sx={{ color: '#94a3b8' }} /></InputAdornment> }} />
                </Grid>
                <Grid item xs={12} md={6}><TextField fullWidth label="Department" name="department" value={formData.department} onChange={handleChange} helperText="e.g., Revenue Department" /></Grid>
                <Grid item xs={12} md={6}><TextField fullWidth label="Posting Location" name="postingLocation" value={formData.postingLocation} onChange={handleChange} helperText="Current posting location" /></Grid>
                <Grid item xs={12} md={6}><TextField fullWidth label="Batch Year" name="batchYear" type="number" value={formData.batchYear} onChange={handleChange} inputProps={{ min: 1950, max: new Date().getFullYear() }} /></Grid>
                <Grid item xs={12} md={6}><TextField fullWidth label="Joining Date" name="joiningDate" type="date" value={formData.joiningDate} onChange={handleChange} InputLabelProps={{ shrink: true }} /></Grid>
                <Grid item xs={12} md={6}><TextField fullWidth label="Grade Pay" name="gradePay" value={formData.gradePay} onChange={handleChange} helperText="e.g., ₹5400" /></Grid>
                <Grid item xs={12} md={6}><TextField fullWidth label="Pay Level" name="payLevel" value={formData.payLevel} onChange={handleChange} helperText="e.g., Level 10" /></Grid>
              </Grid>
            )}

            {/* Step 2: Address & Emergency */}
            {activeStep === 2 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><LocationIcon sx={{ color: '#2563eb' }} /><Typography variant="h6" sx={{ fontWeight: 600 }}>Residence Address</Typography></Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12}><TextField fullWidth label="Address Line 1" name="residenceAddressLine1" value={formData.residenceAddressLine1} onChange={handleChange} helperText="House/Flat No., Building Name" /></Grid>
                        <Grid item xs={12}><TextField fullWidth label="Address Line 2" name="residenceAddressLine2" value={formData.residenceAddressLine2} onChange={handleChange} helperText="Street, Area, Colony" /></Grid>
                        <Grid item xs={12} md={6}><TextField fullWidth label="City" name="residenceCity" value={formData.residenceCity} onChange={handleChange} /></Grid>
                        <Grid item xs={12} md={6}><TextField fullWidth label="District" name="residenceDistrict" value={formData.residenceDistrict} onChange={handleChange} /></Grid>
                        <Grid item xs={12} md={6}><TextField fullWidth label="State" name="residenceState" value={formData.residenceState} onChange={handleChange} /></Grid>
                        <Grid item xs={12} md={6}><TextField fullWidth label="Pincode" name="residencePincode" value={formData.residencePincode} onChange={handleChange} error={!!errors.residencePincode} helperText={errors.residencePincode || '6 digit pincode'} /></Grid>
                        <Grid item xs={12}><TextField fullWidth label="Landmark" name="residenceLandmark" value={formData.residenceLandmark} onChange={handleChange} helperText="Nearby landmark" /></Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
                <Grid item xs={12}>
                  <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><ContactIcon sx={{ color: '#dc2626' }} /><Typography variant="h6" sx={{ fontWeight: 600 }}>Emergency Contact</Typography></Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}><TextField fullWidth label="Contact Name" name="emergencyContactName" value={formData.emergencyContactName} onChange={handleChange} /></Grid>
                        <Grid item xs={12} md={6}><TextField fullWidth label="Relationship" name="emergencyContactRelation" value={formData.emergencyContactRelation} onChange={handleChange} helperText="e.g., Spouse, Father, Mother" /></Grid>
                        <Grid item xs={12}><TextField fullWidth label="Emergency Contact Phone" name="emergencyContactPhone" value={formData.emergencyContactPhone} onChange={handleChange} error={!!errors.emergencyContactPhone} helperText={errors.emergencyContactPhone || '10 digit mobile number'}
                          InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ color: '#94a3b8' }} /></InputAdornment> }} /></Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              </Grid>
            )}

            {/* Step 3: Medical Info */}
            {activeStep === 3 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><MedicalIcon sx={{ color: '#dc2626' }} /><Typography variant="h6" sx={{ fontWeight: 600 }}>Medical Information (Optional)</Typography></Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth><InputLabel>Blood Group</InputLabel>
                            <Select name="bloodGroup" value={formData.bloodGroup} onChange={handleSelectChange} label="Blood Group">
                              <MenuItem value="">Select Blood Group</MenuItem>
                              {BLOOD_GROUPS.map((bg) => (<MenuItem key={bg} value={bg}>{bg}</MenuItem>))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}><TextField fullWidth label="Allergies" name="allergies" value={formData.allergies} onChange={handleChange} helperText="e.g., Penicillin, Peanuts" /></Grid>
                        <Grid item xs={12}><TextField fullWidth label="Chronic Conditions" name="chronicConditions" value={formData.chronicConditions} onChange={handleChange} helperText="e.g., Diabetes, Hypertension" /></Grid>
                        <Grid item xs={12}><TextField fullWidth label="Current Medications" name="currentMedications" value={formData.currentMedications} onChange={handleChange} helperText="List any medications you are currently taking" /></Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              </Grid>
            )}

            {/* Step 4: Review */}
            {activeStep === 4 && (
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#1e293b' }}>Review Your Information</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ mb: 2 }}><CardContent>
                      <Typography variant="subtitle2" sx={{ color: '#64748b', mb: 1 }}>Account Details</Typography>
                      <Typography variant="body2"><strong>Username:</strong> {formData.username}</Typography>
                      <Typography variant="body2"><strong>Email:</strong> {formData.email}</Typography>
                      <Typography variant="body2"><strong>Mobile:</strong> {formData.mobile}</Typography>
                      <Typography variant="body2"><strong>Full Name:</strong> {formData.fullName}</Typography>
                    </CardContent></Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ mb: 2 }}><CardContent>
                      <Typography variant="subtitle2" sx={{ color: '#64748b', mb: 1 }}>Service Details</Typography>
                      <Typography variant="body2"><strong>Employee ID:</strong> {formData.employeeId}</Typography>
                      <Typography variant="body2"><strong>Cadre:</strong> {CADRES.find(c => c.code === formData.cadre)?.name || 'N/A'}</Typography>
                      <Typography variant="body2"><strong>Designation:</strong> {formData.designation}</Typography>
                      <Typography variant="body2"><strong>Posting:</strong> {formData.postingLocation || 'N/A'}</Typography>
                    </CardContent></Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ mb: 2 }}><CardContent>
                      <Typography variant="subtitle2" sx={{ color: '#64748b', mb: 1 }}>Contact & Medical</Typography>
                      <Typography variant="body2"><strong>City:</strong> {formData.residenceCity || 'N/A'}</Typography>
                      <Typography variant="body2"><strong>State:</strong> {formData.residenceState || 'N/A'}</Typography>
                      <Typography variant="body2"><strong>Blood Group:</strong> {formData.bloodGroup || 'N/A'}</Typography>
                      <Typography variant="body2"><strong>Emergency:</strong> {formData.emergencyContactPhone || 'N/A'}</Typography>
                    </CardContent></Card>
                  </Grid>
                </Grid>
                <Alert severity="info" sx={{ mt: 2 }}>By submitting this registration, you confirm that all information is accurate and you agree to our terms of service.</Alert>
              </Box>
            )}

            {/* Navigation Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button disabled={activeStep === 0} onClick={handleBack} startIcon={<BackIcon />}>Back</Button>
              {activeStep < STEPS_OFFICER.length - 1 ? (
                <Button variant="contained" onClick={handleNext} endIcon={<ArrowForwardIcon />}>Next</Button>
              ) : (
                <Button variant="contained" onClick={handleSubmit} disabled={loading} sx={{ minWidth: 150 }}>
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Submit Registration'}
                </Button>
              )}
            </Box>
          </Paper>

          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="body2" sx={{ color: '#64748b' }}>
              Already have an account?{' '}
              <Button
                variant="text"
                size="small"
                onClick={() => navigate('/login')}
                sx={{ color: '#2563eb', textTransform: 'none', fontWeight: 600, p: 0, minWidth: 'auto' }}
              >
                Login here
              </Button>
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        bgcolor: 'transparent' ,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(ellipse at top, rgba(37, 99, 235, 0.05) 0%, transparent 50%)',
        },
      }}
    >
      {/* Left Side - Branding */}
      <Box
        sx={{
          width: { xs: '0', md: '35%' },
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          p: 6,
          background: 'linear-gradient(135deg, #EFF6FF 0%, #F5F3FF 50%, #FFFBEB 100%)',
          position: 'relative',
        }}
      >
        {/* Background Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 30% 20%, rgba(59, 130, 246, 0.1) 0%, transparent 40%)',
            background2: 'radial-gradient(circle at 70% 80%, rgba(16, 185, 129, 0.1) 0%, transparent 40%)',
          }}
        />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}
        >
          {/* Logo */}
          <Box sx={{ mb: 4 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 50%, #F59E0B 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <HealthIcon sx={{ fontSize: 40, color: 'white' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1A1A2E', mb: 1 }}>
              Civilhealth Healthcare
            </Typography>
            <Typography variant="body2" sx={{ color: '#6B7280' }}>
              Government Healthcare Network
            </Typography>
          </Box>

          {/* Main Message */}
          <Typography variant="h3" sx={{ fontWeight: 700, color: '#1A1A2E', mb: 2, lineHeight: 1.2 }}>
            Join Our Network
          </Typography>
          <Typography variant="body1" sx={{ color: '#374151', mb: 4, maxWidth: 400, mx: 'auto' }}>
            Register to access premium healthcare services designed for government officers and their families.
          </Typography>

          {/* Features */}
          <Box sx={{ textAlign: 'left', maxWidth: 400, mx: 'auto' }}>
            {[
              { icon: <VerifiedIcon />, text: 'Verified Healthcare Professionals' },
              { icon: <SecurityIcon />, text: 'Secure & Confidential Records' },
              { icon: <StarIcon />, text: 'Premium Healthcare Services' },
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 + index * 0.1 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Box sx={{ color: '#10B981' }}>{feature.icon}</Box>
                  <Typography variant="body2" sx={{ color: '#374151' }}>{feature.text}</Typography>
                </Box>
              </motion.div>
            ))}
          </Box>
        </motion.div>
      </Box>

      {/* Right Side - Role Selection */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 3, md: 6 },
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          style={{ width: '100%', maxWidth: 800 }}
        >
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 5 },
              borderRadius: 4,
              bgcolor: 'white',
              border: '1px solid #E2E8F0',
            }}
          >
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#0F172A', mb: 1 }}>
                Choose Your Role
              </Typography>
              <Typography variant="body1" sx={{ color: '#64748B' }}>
                Select your registration type to get started
              </Typography>
            </Box>

            {/* Role Cards */}
            <Grid container spacing={3}>
              {roles.map((role, index) => (
                <Grid item xs={12} md={6} key={role.id}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 + index * 0.1 }}
                  >
                    <Card
                      sx={{
                        height: '100%',
                        borderRadius: 3,
                        border: '2px solid transparent',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        position: 'relative',
                        overflow: 'visible',
                        '&:hover': {
                          borderColor: role.color,
                          transform: 'translateY(-4px)',
                          boxShadow: `0 12px 40px rgba(0, 0, 0, 0.12)`,
                          '& .role-icon': {
                            transform: 'scale(1.1)',
                          },
                        },
                      }}
                      onClick={() => role.onClick()}
                    >
                      {/* Premium Badge */}
                      {role.premium && (
                        <Chip
                          label="Premium"
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: -10,
                            right: 16,
                            bgcolor: 'transparent' ,
                            color: '#059669',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                          }}
                        />
                      )}

                      <CardContent sx={{ p: 3 }}>
                        {/* Icon */}
                        <Box
                          className="role-icon"
                          sx={{
                            width: 72,
                            height: 72,
                            borderRadius: 3,
                            background: role.gradient,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 2,
                            color: 'white',
                            transition: 'transform 0.3s ease',
                          }}
                        >
                          {role.icon}
                        </Box>

                        {/* Title */}
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#0F172A', mb: 0.5 }}>
                          {role.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: role.color, mb: 1.5, fontWeight: 500 }}>
                          {role.subtitle}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748B', mb: 2 }}>
                          {role.description}
                        </Typography>

                        <Divider sx={{ my: 2 }} />

                        {/* Features */}
                        <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600, display: 'block', mb: 1 }}>
                          FEATURES
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                          {role.features.slice(0, 3).map((feature, i) => (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: role.color }} />
                              <Typography variant="caption" sx={{ color: '#475569' }}>
                                {feature}
                              </Typography>
                            </Box>
                          ))}
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        {/* Requirements */}
                        <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600, display: 'block', mb: 1 }}>
                          REQUIREMENTS
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                          {role.requirements.slice(0, 2).map((req, i) => (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'transparent' }} />
                              <Typography variant="caption" sx={{ color: '#475569' }}>
                                {req}
                              </Typography>
                            </Box>
                          ))}
                        </Box>

                        {/* Action Button */}
                        <Button
                          fullWidth
                          variant="contained"
                          endIcon={<ArrowForwardIcon />}
                          sx={{
                            mt: 1,
                            py: 1.5,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            background: role.gradient,
                            '&:hover': {
                              background: role.gradient,
                              boxShadow: `0 6px 20px ${role.color}40`,
                            },
                          }}
                        >
                          Register as {role.id === 'doctor' ? 'Doctor' : 'Officer'}
                        </Button>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>

            {/* Footer */}
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Typography variant="body2" sx={{ color: '#64748B' }}>
                Already have an account?{' '}
                <Button
                  variant="text"
                  size="small"
                  onClick={() => navigate('/login')}
                  sx={{ color: '#2563EB', textTransform: 'none', fontWeight: 600, p: 0, minWidth: 'auto' }}
                >
                  Login here
                </Button>
              </Typography>
            </Box>

            {/* Help Text */}
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                Need help? Contact support at{' '}
                <Typography component="span" sx={{ color: '#2563EB', fontWeight: 500 }}>
                  support@civilhealth.gov.in
                </Typography>
              </Typography>
            </Box>
          </Paper>
        </motion.div>
      </Box>
    </Box>
  );
};

export default RegisterPage;
